import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BulkSelectEmployeeDialogComponent } from '@app/components';
import { getAllFileTypes } from '@app/functions/files/get-all-file-types';
import { TrainingProgram } from '@app/models/training/training-program.model';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AuthService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { File as FileModel } from '@models/common/file.model';
import { Employee } from '@models/employee/employee.model';
import { FormFileManagerService } from '@training/services/form-file-manager.service';
import { EditEnrolledEmployeesTable } from '@training/services/tables/edit-enrolled-employees/edit-enrolled-employees.table';
import moment from 'moment';

@Component({
    selector: 'app-form-training-program',
    templateUrl: './training-program.template.html',
    styleUrls: ['./training-program.style.scss'],
})
export class TrainingProgramForm extends BaseForm implements OnInit {
    @ViewChild('selectEmployees', { static: true }) selectEmployees: BulkSelectEmployeeDialogComponent;
    @Input() program: TrainingProgram;
    @Output() saveProgramEvent: EventEmitter<any> = new EventEmitter<any>();

    toolTipDueDateText = 'forms.training.toolTipDueDateText';
    toolTipFrequentRecurrenceTypeText = 'forms.training.toolTipFrequentRecurrenceTypeText';
    toolTipExpiredRecurrenceTypeText = 'forms.training.toolTipExpiredRecurrenceTypeText';
    allowedFileTypes = getAllFileTypes();

    months = moment.monthsShort();
    table: EditEnrolledEmployeesTable;
    protected _hasRecurrence = false;
    protected _hasMaxOccurrences = false;
    protected _hasDueDate = false;
    protected cache = {};
    protected originalEnrolledIds: number[];
    protected allEnrolled: Employee[];

    constructor(
        protected auth: AuthService,
        public fileManager: FormFileManagerService
    ) {
        super();
    }

    set hasDueDate(isEnabled: boolean) {
        if (isEnabled === this._hasDueDate) {
            return;
        }
        this.toggleDueData(isEnabled);
        this._hasDueDate = isEnabled;
    }

    get hasDueDate(): boolean {
        return this._hasDueDate;
    }

    get daysOfMonth(): number[] {
        return new Array(
            moment()
                .month(this.program.recurrenceAnnualMonth - 1)
                .daysInMonth()
        );
    }

    get hasMaxOccurrences(): boolean {
        return this._hasMaxOccurrences;
    }

    set hasMaxOccurrences(isEnabled: boolean) {
        this._hasMaxOccurrences = isEnabled;
        this.cacheMaxOccurrences();
    }

    get hasRecurrence(): boolean {
        return this._hasRecurrence;
    }

    set hasRecurrence(isEnabled: boolean) {
        if (!isEnabled) {
            this.cacheProgramState(
                [
                    'recurrenceType',
                    'frequencyType',
                    'frequencyOffset',
                    'recurrenceAnnualDay',
                    'recurrenceAnnualMonth',
                    'maxOccurrences',
                ],
                true
            );
        } else {
            // default recurrence is annual
            this.applyProgramState('recurrenceType');
            this.cacheMaxOccurrences();
        }
        this.onRecurrenceTypeChange();
        this._hasRecurrence = isEnabled;
    }

    ngOnInit(): void {
        this._hasDueDate = this.program.hasDueDate || this._hasDueDate;
        this._hasMaxOccurrences = this.program.maxOccurrences > 0;
        this._hasRecurrence = this.program.isRecurring || this._hasRecurrence;
        this.table = new EditEnrolledEmployeesTable(this.auth);
        this.initializeEmployees();
        this.fileManager.setFileModels(this.program.files);
        this.setupCache();
    }

    onFileSelected(files: FileList): void {
        this.fileManager.addInputFiles(files);
    }

    onFileModelRemoved(fileModel: FileModel): void {
        this.fileManager.removeFileModel(fileModel);
    }

    onFileRemoved(file: File): void {
        this.fileManager.removeInputFile(file);
    }

    onRecurrenceTypeChange(): void {
        this.cacheProgramState(['frequencyOffset', 'frequencyType', 'recurrenceAnnualDay', 'recurrenceAnnualMonth']);
        if (this.program.recurrenceType === 'frequency') {
            this.applyProgramState('frequencyOffset');
            this.applyProgramState('frequencyType', 'day');
            this.program.recurrenceAnnualDay = null;
            this.program.recurrenceAnnualMonth = null;
            this.program.hasExpirationDate = false;

            return;
        }

        if (this.program.recurrenceType === 'annual') {
            this.applyProgramState(['recurrenceAnnualDay', 'recurrenceAnnualMonth']);
            this.program.frequencyOffset = null;
            this.program.frequencyType = null;
            this.program.hasExpirationDate = false;

            return;
        }

        if (this.program.recurrenceType === 'expiration') {
            this.applyProgramState(['hasExpirationDate']);
            this.program.frequencyOffset = null;
            this.program.frequencyType = null;
            this.program.recurrenceAnnualDay = null;
            this.program.recurrenceAnnualMonth = null;

            return;
        }

        // not in frequency or annual or expiration, remove the data
        this.program.hasExpirationDate = false;
        this.program.frequencyOffset = null;
        this.program.frequencyType = null;
        this.program.recurrenceAnnualDay = null;
        this.program.recurrenceAnnualMonth = null;
    }

    enableDueDate(): void {
        this.hasDueDate = true;
    }

    onAssignClick(): void {
        if (this.table === null) {
            this.table = new EditEnrolledEmployeesTable(this.auth);
        }

        this.selectEmployees.show<Employee[]>(this.allEnrolled).then((selected: Employee[]) => {
            if (selected === null) {
                return;
            }
            this.allEnrolled = selected;
            this.updateProgramAttachments();
        });
    }

    unEnrollAction(event: MenuColumnEvent): void {
        const remove = event.data as Employee;
        this.allEnrolled = this.allEnrolled.filter((employee: Employee) => employee.id !== remove.id);
        this.updateProgramAttachments();
    }

    protected setupCache(): void {
        this.cacheProgramState([
            'recurrenceType',
            'frequencyType',
            'frequencyOffset',
            'recurrenceAnnualDay',
            'recurrenceAnnualMonth',
            'maxOccurrences',
        ]);
    }

    /**
     * Store the current state of one or more program model attributes.
     * Optionally can set that attribute to null - useful when un-setting a cascading form input.
     * Note - if the program value is NULL, the cache value is preserved. This is multiple option form
     * input, where there is not a binary true/false, null/string value.
     */
    protected cacheProgramState(keys: string | string[], clearProgram = false): void {
        if (keys instanceof Array) {
            return keys.forEach((key) => this.cacheProgramState(key, clearProgram));
        }
        const key = keys;
        this.cache[key] = this.program.getAttribute(key) || this.cache[key];
        if (clearProgram) {
            this.program[key] = null;
        }
    }

    /**
     * Set a value (or values) from cache on the program model. Can set a default if the cached state is null.
     */
    protected applyProgramState(keys: string | string[], defaultValue: string = null): void {
        if (keys instanceof Array) {
            return keys.forEach((key) => this.applyProgramState(key, defaultValue));
        }
        this.program[keys] = this.cache[keys] || defaultValue;
    }

    protected cacheMaxOccurrences(): void {
        if (this._hasMaxOccurrences) {
            this.applyProgramState('maxOccurrences');
            return;
        }
        this.cacheProgramState('maxOccurrences', true);
    }

    protected initializeEmployees(): void {
        Employee.param('company', this.auth.company.id)
            .where('trainingProgramId', this.program.id)
            .all()
            .then(([enrolled, _]: [Employee[], object]) => {
                this.allEnrolled = enrolled;
                this.originalEnrolledIds = enrolled.map((e: Employee) => e.id);
                this.table.setEmployeeIds(this.originalEnrolledIds);
            });
    }

    protected updateProgramAttachments(): void {
        const selectedIds = this.allEnrolled.map((e: Employee) => e.id);
        this.program.cleanUp();
        this.program.attach(
            'employees',
            selectedIds.filter((selectedId: number) => {
                return this.originalEnrolledIds.indexOf(selectedId) < 0;
            })
        );

        this.program.detach(
            'employees',
            this.originalEnrolledIds.filter((enrolledId: number) => {
                return selectedIds.indexOf(enrolledId) < 0;
            })
        );
        this.table.setEmployeeIds(this.allEnrolled.map((e: Employee) => e.id));
    }

    protected toggleDueData(isEnabled: boolean): void {
        if (!isEnabled) {
            this.cacheProgramState(['dueType', 'dueOffset'], true);
        } else {
            this.applyProgramState('dueType', 'day');
            this.applyProgramState('dueOffset');
        }
    }
}
